import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/config";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  deleteDoc,
} from "firebase/firestore";

export default function EmployerAssignedTask() {
  const [assignedCategories, setAssignedCategories] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // ✅ Fetch Company ID once user is authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          setLoading(true);
          setError(""); // ✅ Clear any previous errors

          const employerRef = doc(db, "users", user.uid);
          const employerDoc = await getDoc(employerRef);

          if (!employerDoc.exists() || !employerDoc.data().companyId) {
            throw new Error("Company ID not found.");
          }

          setCompanyId(employerDoc.data().companyId);
        } catch (err) {
          console.error("Error fetching company ID:", err.message);
          setError("Company information is missing. Please contact support.");
        } finally {
          setLoading(false);
        }
      } else {
        setError("Employer not authenticated.");
        setCompanyId(null); // Clear companyId to avoid issues
        setLoading(false);
      }
    });

    return () => unsubscribe(); // ✅ Cleanup listener on unmount
  }, []);

  useEffect(() => {
    if (!companyId) return;

    const fetchAssignedCategories = async () => {
      try {
        setLoading(true);
        const employeesRef = collection(db, `companies/${companyId}/employees`);
        const employeeSnapshot = await getDocs(employeesRef);
        const categoryMap = new Map();

        for (const employeeDoc of employeeSnapshot.docs) {
          const employeeId = employeeDoc.id;
          const employeeName = employeeDoc.data().name;

          const tasksRef = collection(
            db,
            `companies/${companyId}/employees/${employeeId}/onboarding_progress`
          );
          const tasksSnapshot = await getDocs(tasksRef);

          tasksSnapshot.docs.forEach((taskDoc) => {
            const taskData = taskDoc.data();
            const { categoryName, status } = taskData;

            // Group tasks under category for each employee
            const key = `${employeeId}-${categoryName}`;
            if (!categoryMap.has(key)) {
              categoryMap.set(key, {
                employeeId,
                employeeName,
                categoryName,
                status,
              });
            }
          });
        }

        setAssignedCategories([...categoryMap.values()]);
      } catch (err) {
        console.error("Error fetching assigned categories:", err.message);
        setError("Failed to fetch assigned categories.");
      } finally {
        setLoading(false);
      }
    };

    fetchAssignedCategories();
  }, [companyId]);

  // ✅ Cancel entire category assignment for an employee
  const handleCancelCategory = async (employeeId, categoryName) => {
    try {
      // ✅ Step 1: Delete all tasks from the employee's onboarding progress
      const tasksRef = collection(
        db,
        `companies/${companyId}/employees/${employeeId}/onboarding_progress`
      );
      const tasksSnapshot = await getDocs(tasksRef);

      // Get all tasks under the category
      const tasksToDelete = tasksSnapshot.docs.filter(
        (taskDoc) => taskDoc.data().categoryName === categoryName
      );

      // Delete each task from onboarding progress
      for (const task of tasksToDelete) {
        await deleteDoc(task.ref);
      }

      // ✅ Step 2: Delete category from onboarding template
      const onboardingTemplatesRef = collection(
        db,
        `companies/${companyId}/onboarding_templates`
      );
      const onboardingTemplatesSnapshot = await getDocs(onboardingTemplatesRef);

      const categoryDoc = onboardingTemplatesSnapshot.docs.find(
        (doc) => doc.data().categoryName === categoryName
      );

      if (categoryDoc) {
        await deleteDoc(
          doc(db, `companies/${companyId}/onboarding_templates`, categoryDoc.id)
        );
      }

      // Update UI after deletion
      setAssignedCategories((prev) =>
        prev.filter(
          (category) =>
            !(
              category.employeeId === employeeId &&
              category.categoryName === categoryName
            )
        )
      );

      alert(
        `Category "${categoryName}" unassigned and removed from onboarding templates.`
      );
    } catch (err) {
      console.error("Error canceling category:", err.message);
      setError("Failed to cancel category.");
    }
  };

  return (
    <div>
      <h1>Assigned Onboarding Categories</h1>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : (
        <table border="1">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Category Name</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {assignedCategories.map((category, index) => (
              <tr key={index}>
                <td>{category.employeeName}</td>
                <td>{category.categoryName}</td>
                <td>{category.status}</td>
                <td>
                  <button
                    onClick={() =>
                      navigate(`/employer-task-progress/${category.employeeId}`)
                    }
                  >
                    📊 View Progress
                  </button>
                  <button
                    onClick={() =>
                      handleCancelCategory(
                        category.employeeId,
                        category.categoryName
                      )
                    }
                    style={{
                      marginLeft: "10px",
                      backgroundColor: "red",
                      color: "white",
                    }}
                  >
                    ❌ Cancel Assignment
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
